import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import type { Shop, ShopCategory, ShopProduct, ShopProductImage } from "@/types/api";

const BASE = "/shop";

// ── Shop ─────────────────────────────────────────────────────────────────────

export const useMyShop = () =>
  useQuery({
    queryKey: ["shop", "mine"],
    queryFn: async () => {
      const { data } = await api.get<Shop>(`${BASE}/mine`);
      return data;
    },
    retry: (count, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

export const useCreateShop = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      handle: string;
      name: string;
      tagline?: string;
      hero_title?: string;
      hero_subtitle?: string;
      instagram_url?: string;
      whatsapp_url?: string;
      email?: string;
    }) => {
      const { data } = await api.post<Shop>(`${BASE}/`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

export const useUpdateShop = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<{
      name: string;
      tagline: string;
      hero_title: string;
      hero_subtitle: string;
      instagram_url: string;
      whatsapp_url: string;
      email: string;
      active: boolean;
    }>) => {
      const { data } = await api.patch<Shop>(`${BASE}/${handle}`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

export const useDeployShop = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ url: string }>(`${BASE}/${handle}/deploy`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

export const useDeleteShop = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`${BASE}/${handle}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

export const useUploadShopLogo = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ r2_key: string; url: string }>(
        `${BASE}/${handle}/media/logo`,
        fd,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

export const useUploadShopHero = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ r2_key: string; url: string }>(
        `${BASE}/${handle}/media/hero`,
        fd,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", "mine"] });
    },
  });
};

// ── Categories ────────────────────────────────────────────────────────────────

export const useShopCategories = (handle: string) =>
  useQuery({
    queryKey: ["shop", handle, "categories"],
    queryFn: async () => {
      const { data } = await api.get<ShopCategory[]>(`${BASE}/${handle}/categories`);
      return data;
    },
    enabled: !!handle,
  });

export const useCreateCategory = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      handle: string;
      name: string;
      hero_title?: string;
      hero_subtitle?: string;
      position?: number;
    }) => {
      const { data } = await api.post<ShopCategory>(`${BASE}/${handle}/categories`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "categories"] });
    },
  });
};

export const useUpdateCategory = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<{
      name: string;
      hero_title: string;
      hero_subtitle: string;
      position: number;
      active: boolean;
    }>) => {
      const { data } = await api.patch<ShopCategory>(`${BASE}/${handle}/categories/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "categories"] });
    },
  });
};

export const useDeleteCategory = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${BASE}/${handle}/categories/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "categories"] });
    },
  });
};

export const useUploadCategoryImage = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ r2_key: string; url: string }>(
        `${BASE}/${handle}/categories/${id}/media`,
        fd,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "categories"] });
    },
  });
};

export const useReorderCategories = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await api.put(`${BASE}/${handle}/categories/order`, { ids });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "categories"] });
    },
  });
};

// ── Products ──────────────────────────────────────────────────────────────────

export const useShopProducts = (handle: string) =>
  useQuery({
    queryKey: ["shop", handle, "products"],
    queryFn: async () => {
      const { data } = await api.get<ShopProduct[]>(`${BASE}/${handle}/products`);
      return data;
    },
    enabled: !!handle,
  });

export const useCreateProduct = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      price_points: number;
      slug?: string;
      description?: string;
      description_long?: string;
      category_id?: string;
      featured?: boolean;
      position?: number;
    }) => {
      const { data } = await api.post<ShopProduct>(`${BASE}/${handle}/products`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useUpdateProduct = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<{
      name: string;
      price_points: number;
      slug: string;
      description: string;
      description_long: string;
      category_id: string;
      featured: boolean;
      position: number;
      active: boolean;
    }>) => {
      const { data } = await api.patch<ShopProduct>(`${BASE}/${handle}/products/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useDeleteProduct = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${BASE}/${handle}/products/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useUploadProductImage = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ r2_key: string; url: string }>(
        `${BASE}/${handle}/products/${id}/media`,
        fd,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useUploadProductGalleryImage = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<ShopProductImage>(
        `${BASE}/${handle}/products/${id}/gallery`,
        fd,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useDeleteProductGalleryImage = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, imageId }: { productId: string; imageId: string }) => {
      await api.delete(`${BASE}/${handle}/products/${productId}/gallery/${imageId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};

export const useReorderProductGallery = (handle: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, ids }: { productId: string; ids: string[] }) => {
      await api.put(`${BASE}/${handle}/products/${productId}/gallery/order`, { ids });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shop", handle, "products"] });
    },
  });
};
